import React, { FC } from 'react';

import styled from 'styled-components';

import { RoomViewModel } from '@api/index';
import { Available, AvailableLimited } from '@assets/icons';

import {
  FeatureGroupHeader,
  FeatureLegend,
  Feature,
  FeatureName,
  FeatureNameDefine,
  FeatureGroupContainer,
  FeatureGroupTitle,
  FeatureGroupDescription,
  FeatureListContainer,
  CustomShowMoreText,
  RoomDimension,
  Bullet,
} from './styled';
import {
  HotelFeatureGroup,
  HotelFeaturesMeta,
  RoomFeaturesMeta,
  Entity,
} from './types';

export const FeatureLegendSection = () => (
  <FeatureGroupHeader>
    <span />
    <FeatureLegend>
      <Feature>
        <Available />
        <FeatureNameDefine>Ücretsiz</FeatureNameDefine>
      </Feature>
      <Feature>
        <AvailableLimited />
        <FeatureNameDefine>Ücretli</FeatureNameDefine>
      </Feature>
    </FeatureLegend>
  </FeatureGroupHeader>
);

const ShowMoreLink = styled.span`
  text-decoration: underline;
`;
export const FeatureGroup: FC<{
  features: HotelFeatureGroup['features'];
  Container?: React.ComponentType<React.PropsWithChildren<{}>>;
  Header?: React.ComponentType<React.PropsWithChildren<{}>>;
  name?: string;
  renderDescription: (description?: string) => React.ReactNode;
  description?: string;
  entityType: NonNullable<Entity>;
}> = ({
  name,
  features,
  Container = FeatureGroupContainer,
  Header = FeatureGroupHeader,
  description,
  entityType,
  renderDescription,
}) => {
  return (
    <Container>
      <Header>
        <FeatureGroupTitle>{name}</FeatureGroupTitle>
      </Header>
      {renderDescription(description)}
      <FeatureList features={features} entityType={entityType} />
    </Container>
  );
};

export const FeatureList: FC<
  Pick<HotelFeatureGroup, 'features'> & { entityType: NonNullable<Entity> }
> = ({ features, entityType }) => (
  <FeatureListContainer entityType={entityType}>
    {features.map((feat) => (
      <Feature key={feat.name}>
        {feat.hasCharged ? <AvailableLimited /> : <Available />}
        <FeatureName entityType={entityType} title={feat.name || ''}>
          {feat.name}
        </FeatureName>
      </Feature>
    ))}
  </FeatureListContainer>
);

type WrapperProps = {
  Container: React.ComponentType;
  Header: React.ComponentType;
};

const renderHotelFeatureDescription = (description?: string) => {
  {
    return description ? (
      <CustomShowMoreText
        lines={2}
        more={<ShowMoreLink>Devamını Oku</ShowMoreLink>}
        less=''
        expanded={false}
        width={450}
      >
        <FeatureGroupDescription
          dangerouslySetInnerHTML={{ __html: description }}
        />
      </CustomShowMoreText>
    ) : null;
  }
};
export const HotelFeatures: FC<
  Pick<HotelFeaturesMeta, 'features'> & WrapperProps
> = ({ features, Container, Header }) => {
  return (
    <>
      {features.map((data) => (
        <FeatureGroup
          features={data.features}
          name={data.name}
          description={data.description}
          key={data.id}
          Container={Container}
          Header={Header}
          renderDescription={renderHotelFeatureDescription}
          entityType='hotel'
        />
      ))}
    </>
  );
};

const getAreaDescription = ({ minAreaSize, maxAreaSize }: RoomViewModel) => {
  if (minAreaSize === undefined && maxAreaSize === undefined) return '';
  if (minAreaSize && maxAreaSize) return `${minAreaSize}-${maxAreaSize}`;
  return `${minAreaSize || maxAreaSize}`;
};

const renderRoomFeatureDescription = (areaDescription?: string) =>
  areaDescription ? (
    <RoomDimension>
      <Bullet />
      <span>
        Oda Büyüklüğü: {areaDescription} m<sup>2</sup>
      </span>
    </RoomDimension>
  ) : null;

export const RoomFeatures: FC<
  Pick<RoomFeaturesMeta, 'features'> & {
    room?: RoomViewModel;
    name?: string;
    entityType?: 'room' | 'roomOptions';
  }
> = ({ features, room, entityType = 'room', name = 'Oda Özellikleri' }) => {
  const description = room ? getAreaDescription(room) : '';
  return (
    <FeatureGroup
      features={features}
      name={name}
      renderDescription={renderRoomFeatureDescription}
      description={description}
      entityType={entityType}
    />
  );
};
